import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { menuItems, ROLES } from '../../utils/constants';
import { Icons } from '../../utils/icons';

const ROLES_UI = [
  { value: ROLES.ADMIN,       label: 'Admin' },
  { value: ROLES.COORDINADOR, label: 'Coordinador' },
  { value: ROLES.ATENCION,    label: 'Atención' },
];

// Iconos disponibles para elegir al crear un grupo personalizado (subconjunto
// razonable del catálogo completo de Icons — no todos tienen sentido como
// icono de "carpeta").
const ICONOS_GRUPO = ['Layout', 'Package', 'Tag', 'Wrench', 'Users', 'Calendar', 'Bell', 'Map', 'DollarSign', 'MessageCircle', 'Truck', 'Zap', 'Lock', 'FileText', 'BookOpen'];

// Aplana menuItems (top-level + subitems) para listarlos en la tabla,
// conservando el id del padre original (para saber el valor por defecto de
// "aparece dentro de" cuando no hay override).
function aplanarParaEditor(items) {
  const filas = [];
  for (const item of items) {
    filas.push({ id: item.id, label: item.label, icon: item.icon, roles: item.roles, parentOriginalId: null, esTopLevel: true, grupoOriginal: null, esVirtual: false });
    for (const sub of item.subitems || []) {
      filas.push({ id: sub.id, label: sub.label, icon: sub.icon, roles: sub.roles, parentOriginalId: item.id, esTopLevel: false, grupoOriginal: item.label, esVirtual: false });
    }
  }
  return filas;
}

// Ordena `filas` según `orden` (ids no listados van al final, conservando su
// orden relativo original) — mismo criterio que construirArbolMenu.js.
function ordenarFilas(filas, orden) {
  const posDe = new Map((orden || []).map((id, i) => [id, i]));
  return filas
    .map((f, i) => ({ f, i }))
    .sort((a, b) => {
      const pa = posDe.has(a.f.id) ? posDe.get(a.f.id) : orden.length + a.i;
      const pb = posDe.has(b.f.id) ? posDe.get(b.f.id) : orden.length + b.i;
      return pa - pb;
    })
    .map(x => x.f);
}

const OVERRIDES_VACIOS = { visibilidadPorRol: {}, parentOverride: {}, orden: [], gruposVirtuales: {} };

export default function MenuAdminView() {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState(OVERRIDES_VACIOS);
  const [loading, setLoading] = useState(true);
  const [guardandoId, setGuardandoId] = useState(null);
  const [error, setError] = useState('');
  const [nuevoGrupoNombre, setNuevoGrupoNombre] = useState('');
  const [nuevoGrupoIcono, setNuevoGrupoIcono] = useState(ICONOS_GRUPO[0]);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'configuracion', 'menuAdmin'),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setOverrides({
          visibilidadPorRol: data.visibilidadPorRol || {},
          parentOverride: data.parentOverride || {},
          orden: data.orden || [],
          gruposVirtuales: data.gruposVirtuales || {},
        });
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const filasCatalogo = useMemo(() => aplanarParaEditor(menuItems), []);
  const filas = useMemo(() => {
    const filasGrupos = Object.entries(overrides.gruposVirtuales).map(([id, g]) => ({
      id, label: g.label, icon: g.icon, roles: undefined,
      parentOriginalId: null, esTopLevel: true, grupoOriginal: null, esVirtual: true,
    }));
    return ordenarFilas([...filasCatalogo, ...filasGrupos], overrides.orden);
  }, [filasCatalogo, overrides.gruposVirtuales, overrides.orden]);
  const contenedores = useMemo(() => [
    ...menuItems.map(i => ({ id: i.id, label: i.label })),
    ...Object.entries(overrides.gruposVirtuales).map(([id, g]) => ({ id, label: `${g.label} (grupo)` })),
  ], [overrides.gruposVirtuales]);

  const rolesEfectivos = (fila) => overrides.visibilidadPorRol[fila.id] ?? fila.roles ?? [];
  const padreEfectivo = (fila) =>
    Object.prototype.hasOwnProperty.call(overrides.parentOverride, fila.id) ? overrides.parentOverride[fila.id] : fila.parentOriginalId;
  const estaModificado = (fila) =>
    Object.prototype.hasOwnProperty.call(overrides.visibilidadPorRol, fila.id) ||
    Object.prototype.hasOwnProperty.call(overrides.parentOverride, fila.id);

  async function guardar(nuevoOverrides, idEnGuardado) {
    const previo = overrides;
    const completo = { ...overrides, ...nuevoOverrides };
    setGuardandoId(idEnGuardado); setError('');
    setOverrides(completo); // aplica al instante
    try {
      const resp = await fetch('/.netlify/functions/updateMenuAdmin', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ adminEmail: user.email, ...completo }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
    } catch (e) { setError(e.message); setOverrides(previo); }
    finally { setGuardandoId(null); }
  }

  const toggleRol = (fila, rol) => {
    const actuales = rolesEfectivos(fila);
    const nuevosRoles = actuales.includes(rol) ? actuales.filter(r => r !== rol) : [...actuales, rol];
    guardar({ visibilidadPorRol: { ...overrides.visibilidadPorRol, [fila.id]: nuevosRoles } }, fila.id);
  };

  const cambiarPadre = (fila, nuevoPadreId) => {
    guardar({ parentOverride: { ...overrides.parentOverride, [fila.id]: nuevoPadreId || null } }, fila.id);
  };

  const restablecer = (fila) => {
    const visibilidadPorRol = { ...overrides.visibilidadPorRol };
    delete visibilidadPorRol[fila.id];
    const parentOverride = { ...overrides.parentOverride };
    delete parentOverride[fila.id];
    guardar({ visibilidadPorRol, parentOverride }, fila.id);
  };

  const mover = (fila, direccion) => {
    const ids = filas.map(f => f.id);
    const idx = ids.indexOf(fila.id);
    const nuevoIdx = idx + direccion;
    if (nuevoIdx < 0 || nuevoIdx >= ids.length) return;
    const nuevoOrden = [...ids];
    [nuevoOrden[idx], nuevoOrden[nuevoIdx]] = [nuevoOrden[nuevoIdx], nuevoOrden[idx]];
    guardar({ orden: nuevoOrden }, fila.id);
  };

  const crearGrupo = () => {
    const nombre = nuevoGrupoNombre.trim();
    if (!nombre) return;
    const id = `grupo_${Date.now()}`;
    guardar({ gruposVirtuales: { ...overrides.gruposVirtuales, [id]: { label: nombre, icon: nuevoGrupoIcono } } }, id);
    setNuevoGrupoNombre('');
  };

  const eliminarGrupo = (fila) => {
    const gruposVirtuales = { ...overrides.gruposVirtuales };
    delete gruposVirtuales[fila.id];
    const visibilidadPorRol = { ...overrides.visibilidadPorRol };
    delete visibilidadPorRol[fila.id];
    const parentOverride = { ...overrides.parentOverride };
    delete parentOverride[fila.id];
    guardar({ gruposVirtuales, visibilidadPorRol, parentOverride }, fila.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
        Cargando editor de menú...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1">
          <Icons.Lock className="w-6 h-6 text-red-600" />
          Editor de menú
        </h2>
        <p className="text-gray-500 text-sm">Controla qué rol ve cada sección, dentro de qué grupo aparece y en qué orden. Los cambios se aplican al instante para todo el mundo.</p>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>}

      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <h3 className="text-sm font-bold text-gray-800 mb-3">Crear grupo personalizado</h3>
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={nuevoGrupoNombre} onChange={e => setNuevoGrupoNombre(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && crearGrupo()}
            placeholder="Nombre del grupo (ej. Comercial)"
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-red-400 bg-white flex-1 min-w-[180px]" />
          <select value={nuevoGrupoIcono} onChange={e => setNuevoGrupoIcono(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-red-400 bg-white">
            {ICONOS_GRUPO.map(icono => <option key={icono} value={icono}>{icono}</option>)}
          </select>
          <button onClick={crearGrupo} disabled={!nuevoGrupoNombre.trim()}
            className="text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-4 py-2">
            + Nuevo grupo
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Un grupo vacío (sin secciones dentro) no aparece en el menú de nadie.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="py-3 px-2 text-xs font-bold text-gray-500 uppercase tracking-wide">Orden</th>
              <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Sección</th>
              {ROLES_UI.map(r => (
                <th key={r.value} className="text-center py-3 px-3 text-xs font-bold text-gray-500 uppercase tracking-wide">{r.label}</th>
              ))}
              <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">Aparece dentro de</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filas.map((fila, index) => {
              const IconComponent = Icons[fila.icon];
              const roles = rolesEfectivos(fila);
              const padre = padreEfectivo(fila);
              const modificado = estaModificado(fila);
              const guardandoEsta = guardandoId === fila.id;
              return (
                <tr key={fila.id} className={`hover:bg-gray-50 ${guardandoEsta ? 'opacity-50' : ''} ${fila.esVirtual ? 'bg-cyan-50/40' : ''}`}>
                  <td className="py-2.5 px-2">
                    <div className="flex flex-col items-center gap-0.5">
                      <span role="button" tabIndex={0} onClick={() => mover(fila, -1)}
                        className={`p-0.5 rounded ${index === 0 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-200 cursor-pointer'}`}>
                        <Icons.ChevronDown className="w-3.5 h-3.5 rotate-180" />
                      </span>
                      <span role="button" tabIndex={0} onClick={() => mover(fila, 1)}
                        className={`p-0.5 rounded ${index === filas.length - 1 ? 'text-gray-200' : 'text-gray-400 hover:bg-gray-200 cursor-pointer'}`}>
                        <Icons.ChevronDown className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      {!fila.esTopLevel && <span className="text-gray-300 flex-shrink-0">↳</span>}
                      {IconComponent && <IconComponent className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      <span className={`font-semibold ${fila.esTopLevel ? 'text-gray-900' : 'text-gray-700'}`}>{fila.label}</span>
                      {fila.esVirtual && <span className="text-xs font-bold text-cyan-700 bg-cyan-100 rounded-full px-1.5 py-0.5 flex-shrink-0">grupo</span>}
                      {modificado && <span className="text-xs font-bold text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5 flex-shrink-0">editado</span>}
                    </div>
                  </td>
                  {ROLES_UI.map(r => (
                    <td key={r.value} className="text-center py-2.5 px-3">
                      <input type="checkbox" checked={roles.includes(r.value)} disabled={guardandoEsta}
                        onChange={() => toggleRol(fila, r.value)}
                        className="w-4 h-4 accent-red-600 cursor-pointer" />
                    </td>
                  ))}
                  <td className="py-2.5 px-4">
                    <select value={padre || ''} disabled={guardandoEsta}
                      onChange={e => cambiarPadre(fila, e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-red-400 bg-white">
                      <option value="">— Nivel superior —</option>
                      {contenedores.filter(c => c.id !== fila.id).map(c => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    {fila.esVirtual ? (
                      <button onClick={() => eliminarGrupo(fila)} disabled={guardandoEsta}
                        className="text-xs font-bold text-gray-400 hover:text-red-600 disabled:opacity-40">
                        Eliminar grupo
                      </button>
                    ) : modificado && (
                      <button onClick={() => restablecer(fila)} disabled={guardandoEsta}
                        className="text-xs font-bold text-gray-400 hover:text-red-600 disabled:opacity-40">
                        Restablecer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
